import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { Button, FormLayout, Modal, ModalBody, ModalFooter } from '@customerio/pluma-components/react';
import { Form, TextArea, useForm } from '@/components/form';
import { authStore } from '@/services/auth/store';
import { useStore } from '@nanostores/react';
import { z } from 'zod';
import { useSubmitFeedback } from '../hooks/use-submit-feedback';

interface GeneralFeedbackModalProps {
  initialFeedback?: string;
}

export function GeneralFeedbackModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<GeneralFeedbackModalProps>) {
  const auth = useStore(authStore);
  const submitFeedback = useSubmitFeedback();

  const form = useForm({
    defaultValues: {
      criticism: data.initialFeedback ?? '',
      praise: '',
    },
    validators: {
      onChange: z
        .object({
          criticism: z.string(),
          praise: z.string(),
        })
        .refine((data) => (data.criticism?.length ?? 0) > 0 || (data.praise?.length ?? 0) > 0, {
          message: 'Please provide either criticism or praise',
          path: ['criticism'],
        }),
    },
    async onSubmit(values) {
      try {
        await submitFeedback({
          ...values.value,
          source: 'General Feedback',
          replyTo: auth.user?.email ?? '',
        });

        onConfirm(values.value);
      } catch {
        // noop
      }
    },
  });

  const handleClose = () => {
    onClose();
  };

  const isSubmitDisabled = form.state.isValid !== true || form.state.isSubmitting;

  if (auth.account == null || auth.user == null) {
    return null;
  }

  return (
    <Modal
      state={modalState}
      onClose={handleClose}
      title="Share Your Feedback"
      size="md"
      shouldCloseOnOverlayClick={false}
    >
      <ModalBody>
        <Form api={form} id="general-feedback-form">
          <FormLayout>
            <TextArea
              api={form}
              name="criticism"
              label="What can we improve?"
              placeholder="Customer.io would be better if…"
              minRows={3}
              maxRows={8}
              id="general-feedback-criticism-textarea"
            />

            <TextArea
              api={form}
              name="praise"
              label="What are we doing well?"
              placeholder="I like…"
              minRows={3}
              maxRows={8}
              id="general-feedback-praise-textarea"
            />
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="subtle" onClick={handleClose} isDisabled={form.state.isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" form="general-feedback-form" type="submit" isDisabled={isSubmitDisabled}>
          Send message
        </Button>
      </ModalFooter>
    </Modal>
  );
}
